import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link as RouterLink } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  CardContent,
  ClickAwayListener,
  Grid,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import LocalPoliceIcon from "@mui/icons-material/LocalPolice";

// project import
import MainCard from "components/MainCard";
import Transitions from "components/@extended/Transitions";
import ProfileTab from "./ProfileTab";
import { selectCurrentUser } from "store/user/user.selector";
import { VehicleBiddingApi } from "store/vehicleBidding.api";

// assets
// import avatar1 from 'assets/images/users/avatar-2.png';
import { logoutUser } from "store/user/user.action";
import { toast } from "react-toastify";

// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile = () => {
  // const [userData, setUserData] = useState();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector(selectCurrentUser);

  // User logout function
  const handleLogout = () => {
    try {
      // resets VehicleBiddingAPI's state and caches.
      dispatch(VehicleBiddingApi.util.resetApiState());
      dispatch(logoutUser());
      navigate("/login");
      toast.success(
        "Thank you for using SGCarDeals.com. Hope to see you again soon!"
      );
    } catch (error) {
      console.log(`error with logout - ${error}`);
    }
  };

  const showLogin = () => {
    if (userData) {
      return (
        <ButtonBase
          sx={{
            p: 0.25,
            bgcolor: open ? iconBackColorOpen : "transparent",
            borderRadius: 1,
            "&:hover": { bgcolor: "secondary.lighter" },
          }}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? "profile-grow" : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ p: 0.5 }}
          >
            <Avatar
              alt="profile user"
              src={
                userData.ProfilePicture ||
                "/assets/images/users/avatar-group.png"
              }
              sx={{ width: 32, height: 32 }}
            />
            <Typography variant="subtitle1">
              {userData.UserFirstName} {userData.UserLastName}
            </Typography>
          </Stack>
        </ButtonBase>
      );
    } else {
      return (
        <Button
          variant="contained"
          size="medium"
          component={RouterLink}
          to="/login"
        >
          Login
        </Button>
      );
    }
  };

  const AdminBadge = () => {
    if (userData.UserType === "ADMIN" || userData.UserType === "SADMIN") {
      return (
        <Tooltip title="This user is an admin." arrow>
          <LocalPoliceIcon fontSize="small" color="primary" />
        </Tooltip>
      );
    } else {
      return null;
    }
  };

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const iconBackColorOpen = "grey.300";

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      {showLogin()}
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 9],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            {open && (
              <Paper
                sx={{
                  boxShadow: theme.customShadows.z1,
                  width: 290,
                  minWidth: 240,
                  maxWidth: 290,
                  [theme.breakpoints.down("md")]: {
                    maxWidth: 250,
                  },
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <MainCard elevation={0} border={false} content={false}>
                    <CardContent sx={{ px: 2.5, pt: 3 }}>
                      <Grid container alignItems="center">
                        <Grid item xs={12}>
                          <Stack
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                          >
                            <Avatar
                              alt="profile user"
                              src={
                                userData.ProfilePicture ||
                                "/assets/images/users/avatar-group.png"
                              }
                              sx={{ width: 32, height: 32 }}
                            />
                            <Typography variant="h6" fontWeight={600}>
                              {userData.UserFirstName} {userData.UserLastName}
                            </Typography>
                            <AdminBadge />
                          </Stack>
                        </Grid>
                        {/* <Grid container item xs={3} justifyContent='flex-end'>
                                                    <IconButton size='large' color='secondary' onClick={handleLogout}>
                                                        <LogoutOutlined />
                                                    </IconButton>
                                                </Grid> */}
                      </Grid>
                    </CardContent>

                    {open && (
                      <>
                        <Box sx={{ borderTop: 1, borderColor: "divider" }}>
                          <ProfileTab handleLogout={handleLogout} />
                        </Box>
                      </>
                    )}
                  </MainCard>
                </ClickAwayListener>
              </Paper>
            )}
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Profile;
